import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

// ═══════════════════════════════════════════════════════════════════════════
//  THE TWO THINGS THAT HANG OFF A WHOLE DOCUMENT
//
//  A link to it, and files attached to it. Both belong to the estimate or the
//  invoice itself rather than to any one line, which is why they live here and
//  not in the line editor.
//
//  ── THE LINK ─────────────────────────────────────────────────────────────
//  Plenty of technicians have already written the same estimate in Joist, in
//  QuickBooks, or on a sheet in a shared drive. Retyping it into a second
//  system is how the two end up disagreeing about the price, so the field
//  takes wherever it already lives.
//
//  ── THE FILES ────────────────────────────────────────────────────────────
//  Their own written quote, a supplier's bid, a marked-up photo of the scope.
//  Uploaded as they are picked, so the document itself still posts as plain
//  JSON carrying URLs.
// ═══════════════════════════════════════════════════════════════════════════

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

// Nothing is rejected as they type — a half-typed address is not a mistake —
// but a link that could not possibly work is worth saying so about before they
// send it. The server does the real check and only ever stores http(s).
const looksLikeUrl = (v) => {
  const raw = String(v || '').trim();
  if (!raw) return true;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    return ['http:', 'https:'].includes(u.protocol) && u.hostname.includes('.');
  } catch { return false; }
};

export default function DocExtras({ url, onUrl, files = [], onFiles, kind = 'estimate' }) {
  const { uploadDocuments } = useApp();
  const [busy, setBusy] = useState(false);
  const label = kind === 'invoice' ? 'invoice' : 'estimate';
  const bad = !looksLikeUrl(url);

  const add = async (picked) => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const got = await uploadDocuments(picked);
      if (got.length) onFiles([...(files || []), ...got.map(f => ({ url: f.url, name: f.name, bytes: f.bytes }))]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="form-group">
        <label htmlFor="dx-url">Link to your {label} <span className="dx-opt">(optional)</span></label>
        <p className="dx-hint">Both of these are optional. Send the {label} without them and nothing is held up.</p>
        <input
          id="dx-url"
          type="url"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          value={url || ''}
          onChange={e => onUrl(e.target.value)}
          placeholder={`joist.com/…  ·  where your ${label} already lives`}
        />
        {/* Grey, not red. Nothing here is required and nothing here blocks a
            send — this is a note that what was typed will not become a link,
            not an error to clear before carrying on. */}
        {bad && <p className="dx-warn">That will not save as a link. Leave it blank if you do not have one.</p>}
      </div>

      <div className="form-group">
        <label>Attachments <span className="dx-opt">(optional)</span></label>
        <div className="dx-drop">
          {(files || []).length > 0 && (
            <ul className="dx-files">
              {files.map((f, i) => (
                <li key={i}>
                  <a href={f.url} target="_blank" rel="noopener noreferrer">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3.5H6.5A1.5 1.5 0 005 5v14a1.5 1.5 0 001.5 1.5h11A1.5 1.5 0 0019 19V8.5z" /><path d="M14 3.5V9h5" /></svg>
                    <span className="dx-file-n">{f.name || 'Attachment'}</span>
                    {f.bytes ? <em>{fmtBytes(f.bytes)}</em> : null}
                  </a>
                  <button type="button" aria-label={`Remove ${f.name || 'this file'}`}
                    onClick={() => onFiles(files.filter((_, n) => n !== i))}>×</button>
                </li>
              ))}
            </ul>
          )}

          {/* Bottom right, where Julian asked for it and where a hand already
              is after reading down the page. */}
          <div className="dx-actions">
            <label className={`dx-add${busy ? ' busy' : ''}`}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
              {busy ? 'Uploading…' : 'Add a PDF or photo'}
              <input type="file" multiple hidden disabled={busy}
                onChange={e => { add(Array.from(e.target.files)); e.target.value = ''; }} />
            </label>
          </div>
        </div>
      </div>
    </>
  );
}
